import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import Money from "components/base/InputMoney";
import RegraFiscalEmpresaFormCard from "components/cards/RegraFiscalEmpresaFormCard";
import OrganizeFormCard from "components/cards/OrganizeFormCard";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import { useToast } from "providers/ToastProvider";
import InventoryTab from "components/tabs/InventoryTab";
import { usePermissionModal } from "contexts/PermissionModalContext";

import { CriarProdutoDtoType, produtosBreadcrumbItems } from "data/produto";
import { transformErrors, floatFormat } from "helpers/utils";
import { ChangeEvent, useState } from "react";
import { Col, Form, Row, Alert } from "react-bootstrap";
import { FormProvider, SubmitHandler, useForm, Controller } from "react-hook-form";
import { Navigate, Link, useNavigate } from "react-router-dom";


const NovoProduto = () => {
  const { showPermissionModal, getMutationErrorHandler } = usePermissionModal();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [precoComDesconto, setPrecoComDesconto] = useState<number>(0);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const novoProdutoBreadcrumbItems = [
    {
      label: "Cadastros",
      url: "#!",
    },
    {
      label: "Produtos",
      url: "/cadastros/produtos",
    },
    {
      label: "Novo",
      active: true,
    },
  ];


  const form = useForm<CriarProdutoDtoType>({
    defaultValues: {
      descricao: "",
      codigosDeBarras: [],
      controleEspecial: false,
      fracionado: 1,
      inativo: false,
      //lista: "",
      //receituario: "",
      //tipoControle: "",
      rms: "",

      // Preços globais (padrão para todas as empresas)
      precoCompra: 0,
      valor: 0,
      margem: 0,
      descontoMaximo: 0,
      desconto: 0,
      precoAberto: false,
      usoContinuo: false,

      empresas: [],
    },
  });

  const mutation = useMutation<any, any, CriarProdutoDtoType>({
    mutationFn: (p: CriarProdutoDtoType) => {
      return axios.post("/api/produto", p);
    },

    onError: getMutationErrorHandler(
      "Você não possui permissão para cadastrar este produto",
      (error) => {
        // Handle non-permission related errors
        const formattedErrors = transformErrors(error.response?.data?.errors || {});
        for (const [field, error] of Object.entries(formattedErrors)) {
          form.setError(field as any, error as any);
        }
      }
    ),

    onSuccess: (response) => {
      console.log("Resposta completa do backend:", response); // Log para debug
      const responseData = response.data;
      console.log("Response data:", responseData); // Log para debug

      // Verifica se a resposta indica falha mesmo com status 200
      if (responseData?.isSuccess === false) {
        const errorMessage = responseData?.message || 'Erro ao criar produto';
        setGeneralError(errorMessage);
        return; // Não redireciona
      }

      // Sucesso real - mostra toast e redireciona para lista de produtos
      setGeneralError(null);
      showToast('Produto criado com sucesso!', "success");
    },
  });

  const onSubmit: SubmitHandler<CriarProdutoDtoType> = (data) => {
    setGeneralError(null); // Limpa erro ao submeter
    console.log("Dados do formulário antes do envio:", data); // Log para debug
    mutation.mutate(data);
  };

  if (mutation.isSuccess) {
    return <Navigate to={`/cadastros/produtos/${mutation.data.data.id}`} />;
  }

  return (
    <div>
      <FormProvider {...form}>
        <PageBreadcrumb items={novoProdutoBreadcrumbItems} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex flex-wrap gap-3 flex-between-end mb-5">
            <div>
              <h2 className="mb-2">Cadastrar um produto</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações do produto
              </h5>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="phoenix-secondary"
                type="button"
                as={Link}
                to="/cadastros/produtos"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                loadingPosition="start"
              >
                Salvar
              </Button>
            </div>
          </div>


          <Row className="g-5">
            <Col xs={12} xl={8}>
              {generalError && (
                <Alert variant="outline-danger" className="mb-3" dismissible onClose={() => setGeneralError(null)}>
                  <strong>❌ Erro:</strong> {generalError}
                </Alert>
              )}

              <h4 className="mb-3">Identificação do produto</h4>
              <Row className="g-2">
                <Form.Group
                  as={Col}
                  xs={12}
                  xl={12}
                  className="mb-5"
                  controlId="descricao"
                >
                  <Form.Control
                    placeholder="Descrição do produto"
                    {...form.register("descricao")}
                    required
                    isInvalid={!!form.formState.errors.descricao}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descricao?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <h4 className="mb-3">Preços</h4>
              <Row className="g-3 mb-5">
                <Form.Group as={Col} xs={12} md={4} controlId="precoCompra">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço de Compra *
                  </Form.Label>
                  <Controller
                    name="precoCompra"
                    control={form.control}
                    rules={{
                      required: "Preço de compra é obrigatório",
                      min: { value: 0.01, message: "Preço de compra deve ser maior que zero" }
                    }}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.precoCompra}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const precoCompra = floatFormat(e.target.value);
                          const margem = floatFormat(form.watch("margem"));
                          const valor = precoCompra * (1 + margem / 100);
                          form.setValue("valor", valor);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.precoCompra?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="margem">
                  <Form.Label className="text-body-highlight fw-bold">
                    Margem (%) *
                  </Form.Label>
                  <Controller
                    name="margem"
                    control={form.control}
                    rules={{
                      required: "Margem é obrigatória",
                      min: { value: 0, message: "Margem deve ser maior ou igual a zero" }
                    }}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.margem}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const precoCompra = floatFormat(form.watch("precoCompra"));
                          const margem = floatFormat(e.target.value);
                          const valor = precoCompra * (1 + margem / 100);
                          form.setValue("valor", valor);
                          const desconto = floatFormat(form.watch("desconto") ?? 0);
                          const novoPrecoComDesconto = valor - (valor * desconto / 100);
                          setPrecoComDesconto(novoPrecoComDesconto);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.margem?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="valor">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço de Venda *
                  </Form.Label>
                  <Controller
                    name="valor"
                    control={form.control}
                    rules={{
                      required: "Preço de venda é obrigatório",
                      min: { value: 0.01, message: "Preço de venda deve ser maior que zero" }
                    }}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.valor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const precoCompra = floatFormat(form.watch("precoCompra"));
                          const valor = floatFormat(e.target.value);
                          const margem = ((valor - precoCompra) / precoCompra) * 100;
                          form.setValue("margem", margem);
                          const desconto = floatFormat(form.watch("desconto") ?? 0);
                          const novoPrecoComDesconto = valor - (valor * desconto / 100);
                          setPrecoComDesconto(novoPrecoComDesconto);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.valor?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="desconto">
                  <Form.Label className="text-body-highlight fw-bold">
                    Desconto Automático
                  </Form.Label>
                  <Controller
                    name="desconto"
                    control={form.control}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.desconto}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const desconto = floatFormat(e.target.value);
                          const valor = floatFormat(form.watch("valor"));
                          const novoPrecoComDesconto = valor - (valor * desconto / 100);
                          setPrecoComDesconto(novoPrecoComDesconto);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.desconto?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="descontoMaximo">
                  <Form.Label className="text-body-highlight fw-bold">
                    Desconto Máximo
                  </Form.Label>
                  <Controller
                    name="descontoMaximo"
                    control={form.control}
                    render={({ field }) => (
                      <Money {...field} isInvalid={!!form.formState.errors.descontoMaximo} />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.descontoMaximo?.message}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} xs={12} md={4} controlId="precoComDesconto">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço com Desconto
                  </Form.Label>
                  <Money
                    value={precoComDesconto}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const novoPrecoComDesconto = floatFormat(e.target.value);
                      const valor = floatFormat(form.watch("valor"));

                      if (valor > 0) {
                        const novoDesconto = ((valor - novoPrecoComDesconto) / valor) * 100;
                        const descontoFinal = Math.max(0, novoDesconto); // Garante que não seja negativo
                        form.setValue("desconto", descontoFinal);
                      }

                      setPrecoComDesconto(novoPrecoComDesconto);
                    }}
                  />
                </Form.Group>
                <Form.Group as={Col} xs={12} md={4} controlId="precoMarketplace">
                  <Form.Label className="text-body-highlight fw-bold">
                    Preço para Marketplace
                  </Form.Label>
                  <Controller
                    name="precoMarketplace"
                    control={form.control}
                    render={({ field }) => (
                      <Money
                        {...field}
                        isInvalid={!!form.formState.errors.precoMarketplace}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          field.onChange(e);
                          const valor = floatFormat(e.target.value);
                          form.setValue("precoMarketplace", valor);
                        }}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.valor?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} md={4} controlId="precoAberto">
                  <Form.Label className="text-body-highlight fw-bold d-block">
                    &nbsp;
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="precoAberto"
                    label="Preço Aberto"
                    {...form.register("precoAberto")}
                    isInvalid={!!form.formState.errors.precoAberto}
                    className="text-body-highlight fw-bold"
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.precoAberto?.message}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} xs={12} md={4} controlId="usoContinuo">
                  <Form.Label className="text-body-highlight fw-bold d-block">
                    &nbsp;
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    id="usoContinuo"
                    label="Uso Contínuo"
                    {...form.register("usoContinuo")}
                    isInvalid={!!form.formState.errors.usoContinuo}
                    className="text-body-highlight fw-bold"
                  />
                  <Form.Control.Feedback type="invalid">
                    {form.formState.errors.usoContinuo?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <div>
                <h4 className="mb-3">Inventário</h4>
                <InventoryTab />
              </div>
            </Col>
            <Col xs={12} xl={4}>
              <Row className="g-2">
                <Col xs={12} xl={12}>
                  <OrganizeFormCard className="mb-3" />
                </Col>
                <Col xs={12} xl={12}>
                  <RegraFiscalEmpresaFormCard />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </div>
  );
};

export default NovoProduto;
